import { parseCookies, setCookie, destroyCookie } from 'nookies'
import { GetServerSidePropsContext } from 'next'

import {
  FREE_TRIAL_TYPE, FreeTrialType, SESSION_TIMEOUT, uuidRegex,
} from '@/lib/constants'

const EXPIRE_TRANSACTION = 'expire'

type FreeTrialCampaign = {
  isActive?: boolean
  endDateTime?: string | null
  trialType?: FreeTrialType
}

export type FreeTrialProfile = {
  verifications?: number | null
  transactionIds?: string[]
  enrolledDateTime?: string | null
  expiresDateTime?: string | null
  campaign?: FreeTrialCampaign
  allowedFcraProductIds?: string[]
}

export const isUnlimitedVerifications = (freeTrial?: FreeTrialProfile | null): boolean => (
  Boolean(freeTrial) && freeTrial.verifications === null
)

export const isHeadToHeadTrial = (freeTrial?: FreeTrialProfile | null): boolean => (
  freeTrial?.campaign?.trialType === FREE_TRIAL_TYPE.HeadToHead
)

export const getVerificationsRemaining = (freeTrial?: FreeTrialProfile | null): number => {
  if (!freeTrial || isUnlimitedVerifications(freeTrial)) {
    return 0
  }

  const total = freeTrial.verifications ?? 0
  const used = freeTrial.transactionIds?.length ?? 0

  return Math.max(0, total - used)
}

export const getFreeTrialExpiresAt = (freeTrial?: FreeTrialProfile | null): Date | null => {
  if (!freeTrial) {
    return null
  }

  // The backend owns the head-to-head term length, so the date is read rather than computed here.
  if (freeTrial.expiresDateTime) {
    const expires = new Date(freeTrial.expiresDateTime)
    return Number.isNaN(expires.getTime()) ? null : expires
  }

  if (freeTrial.campaign?.endDateTime) {
    return new Date(freeTrial.campaign.endDateTime)
  }

  return null
}

export const isFreeTrialActive = (freeTrial?: FreeTrialProfile | null): boolean => {
  if (!freeTrial) {
    return false
  }

  if (freeTrial.transactionIds?.includes(EXPIRE_TRANSACTION)) {
    return false
  }

  const isActive = Boolean(freeTrial.campaign?.isActive)
  if (!isActive) {
    return false
  }

  const expiresAt = getFreeTrialExpiresAt(freeTrial)
  if (expiresAt && expiresAt < new Date()) {
    return false
  }

  if (isUnlimitedVerifications(freeTrial)) {
    return true
  }

  return getVerificationsRemaining(freeTrial) > 0
}

// The free trial program is entered through a `/free-trial/:uuid` deep link. The
// uuid has to survive the whole login / create-account flow — including the SSR
// redirects in `_app` — so it rides in a cookie instead of the query string.
export const FREE_TRIAL_COOKIE = 'freeTrialId'

export const isValidFreeTrialId = (id?: string | string[] | null): id is string => (
  typeof id === 'string' && uuidRegex.test(id)
)

export const storeFreeTrialId = (id: string, ctx?: GetServerSidePropsContext) => {
  setCookie(ctx ?? {}, FREE_TRIAL_COOKIE, id, {
    maxAge: SESSION_TIMEOUT,
    path: '/',
    secure: true,
    sameSite: 'lax',
  })
}

export const fetchFreeTrialId = (ctx?: GetServerSidePropsContext) => {
  const id = parseCookies(ctx)[FREE_TRIAL_COOKIE]

  // A hand-edited or truncated cookie should read as "no free trial link" rather
  // than being handed to the enrollment call later on.
  return isValidFreeTrialId(id) ? id : null
}

export const destroyFreeTrialId = (ctx?: GetServerSidePropsContext) => {
  destroyCookie(ctx ?? {}, FREE_TRIAL_COOKIE, { path: '/' })
}

/**
 * Traits for `gridAnalytics.identify` that tie a login or a new account to the
 * free trial campaign the visitor arrived from.
 *
 * @returns `{ freeTrialId }` while a free trial link is stored, otherwise `{}`.
 */
export const getFreeTrialTraits = (): { freeTrialId?: string } => {
  const freeTrialId = fetchFreeTrialId()

  return freeTrialId ? { freeTrialId } : {}
}
